import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { useLookupsDoc } from '@/hooks/useLookupsDoc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { List, ArrowRight, Menu } from 'lucide-react';

function formatGroupName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function LookupsIndex() {
  const { data, isLoading, error } = useLookupsDoc();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Build sidebar sections - flat list since lookups are already groups
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!data) return [];
    
    return [{
      title: 'Lookup Groups',
      slug: 'lookups',
      items: data.groups.map((group) => ({
        title: formatGroupName(group.name),
        slug: group.name,
        href: `/docs/lookups/${group.name}`,
      })),
    }];
  }, [data]);
  
  return (
    <Layout>
      <SEOHead path="/docs/lookups" />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <List className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-3xl font-bold">Lookups</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                Standardized lookup values and code lists for automotive data. 
                Use these consistent values when implementing data standards.
              </p>
            </div>
            
            {/* Loading State */}
            {isLoading && (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <p className="text-destructive">Failed to load lookups. Please try again.</p>
              </div>
            )}
            
            {/* Empty State */}
            {data && data.groups.length === 0 && (
              <div className="text-center py-12 border rounded-lg bg-muted/20">
                <List className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No lookups available</h3>
                <p className="text-muted-foreground">
                  Check back soon for lookup values.
                </p>
              </div>
            )}
            
            {/* Lookup Groups Grid */}
            {data && data.groups.length > 0 && (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {data.groups.map((group) => (
                    <Link 
                      key={group.name} 
                      to={`/docs/lookups/${group.name}`}
                      className="group"
                    >
                      <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                        <CardHeader>
                          <CardTitle className="text-base flex items-center justify-between">
                            <span>{formatGroupName(group.name)}</span>
                            <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {group.count} value{group.count !== 1 ? 's' : ''}
                            </Badge>
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
                
                {/* Stats */}
                <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
                  {data.groups.length} lookup group{data.groups.length !== 1 ? 's' : ''} with {data.totalCount} total value{data.totalCount !== 1 ? 's' : ''}
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle lookups menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            <h3 className="font-semibold mb-4 px-3">Lookups</h3>
            <DocsSidebarNav 
              sections={sidebarSections} 
              onItemClick={() => setSidebarOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
